// AGC: CategoriasSettings - Sub-page of Settings > Sistema > Categorias
// AGC: Shows all default (built-in) categories as read-only + allows adding custom ones.
// AGC: Custom categories share the same localStorage key as UnifiedInventoryPage so they stay in sync.

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Plus, Trash2, Pencil, X, Tag, Package, Building2, Receipt, HelpCircle } from 'lucide-react'
import { TopBar } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { useFarms } from '../../context/FarmContext'
import { useAuth } from '../../hooks/useAuth'
import { persistCustomList } from '../../hooks/useCustomList'
import { INPUT_CATEGORIES } from '../../types/inventory'
import type { CustomListItem } from '../inventory/ManageCategoriesUnitsModal'
import { slugFromLabel } from '../inventory/ManageCategoriesUnitsModal'
import type { Screen } from '../../types'

// AGC: Same storage key as UnifiedInventoryPage so custom categories stay in sync
const STORAGE_KEY_CUSTOM_CATEGORIES = 'droidfarm_custom_categories'

interface CategoriasSettingsProps {
  onBack: () => void
  onNavigate: (screen: Screen) => void
}

/**
 * AGC: Categories management page (Settings > Sistema > Categorias).
 * AGC: Default categories are read-only. Custom categories can be added, edited, or removed.
 */
export const CategoriasSettings: React.FC<CategoriasSettingsProps> = ({ onBack }) => {
  const { t, i18n } = useTranslation()
  const { currentFarm } = useFarms()
  const { user } = useAuth()

  const farmIdForStorage = currentFarm?.id || 'default'

  // AGC: Locale-aware labels for built-in categories
  const currentLang = i18n?.language || 'pt'
  const builtInCategories = useMemo<CustomListItem[]>(
    () =>
      INPUT_CATEGORIES.map((c) => ({
        id: c.id,
        label: currentLang === 'es' ? c.nameEs : currentLang === 'en' ? c.nameEn : c.namePt,
      })),
    [currentLang]
  )

  const [customCategories, setCustomCategories] = useState<CustomListItem[]>([])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editStockTreatment, setEditStockTreatment] = useState<'stock' | 'asset' | 'expense' | 'ask'>('expense')
  const [isAdding, setIsAdding] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [newStockTreatment, setNewStockTreatment] = useState<'stock' | 'asset' | 'expense' | 'ask'>('expense')

  // AGC: Load custom categories from localStorage (same store as UnifiedInventoryPage)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${STORAGE_KEY_CUSTOM_CATEGORIES}_${farmIdForStorage}`)
      if (raw) {
        const parsed = JSON.parse(raw) as CustomListItem[]
        if (Array.isArray(parsed)) setCustomCategories(parsed)
      }
    } catch {
      // AGC: Ignore corrupt stored data
    }
  }, [farmIdForStorage])

  const persist = useCallback(
    (list: CustomListItem[]) => {
      setCustomCategories(list)
      persistCustomList(STORAGE_KEY_CUSTOM_CATEGORIES, farmIdForStorage, list, user?.uid)
    },
    [farmIdForStorage, user?.uid]
  )

  // AGC: Build a unique id from the label, avoiding collisions with built-in and existing custom ids
  const makeUniqueId = (label: string): string => {
    const base = slugFromLabel(label)
    const allIds = new Set([
      ...builtInCategories.map((c) => c.id),
      ...customCategories.map((c) => c.id),
    ])
    let candidate = base
    let n = 0
    while (allIds.has(candidate)) {
      n += 1
      candidate = `${base}_${n}`
    }
    return candidate
  }

  const handleSaveAdd = () => {
    const label = newLabel.trim()
    if (!label) return
    persist([...customCategories, { id: makeUniqueId(label), label, stockTreatment: newStockTreatment }])
    setIsAdding(false)
    setNewLabel('')
    setNewStockTreatment('expense')
  }

  const handleSaveEdit = () => {
    if (!editingId) return
    const label = editLabel.trim()
    if (!label) return
    persist(customCategories.map((c) => (c.id === editingId ? { ...c, label, stockTreatment: editStockTreatment } : c)))
    setEditingId(null)
    setEditLabel('')
    setEditStockTreatment('expense')
  }

  const handleRemove = (id: string) => {
    persist(customCategories.filter((c) => c.id !== id))
  }

  // AGC: Stock treatment options for the selector
  const treatmentOptions: Array<{ value: 'stock' | 'asset' | 'expense' | 'ask'; labelKey: string; icon: React.ReactNode; desc: string }> = [
    { value: 'stock', labelKey: 'profile.trackingStock', icon: <Package className="w-3.5 h-3.5" />, desc: t('profile.trackingStockDesc', 'Compras adicionam ao estoque, vendas retiram') },
    { value: 'asset', labelKey: 'profile.trackingAsset', icon: <Building2 className="w-3.5 h-3.5" />, desc: t('profile.trackingAssetDesc', 'Cria ou remove ativo de longo prazo') },
    { value: 'expense', labelKey: 'profile.trackingExpense', icon: <Receipt className="w-3.5 h-3.5" />, desc: t('profile.trackingExpenseDesc', 'Apenas registro financeiro') },
    { value: 'ask', labelKey: 'profile.trackingAsk', icon: <HelpCircle className="w-3.5 h-3.5" />, desc: t('profile.trackingAskDesc', 'Pergunta a cada transacao') },
  ]

  // AGC: Reusable treatment selector component
  const TreatmentSelector = ({ value, onChange }: { value: 'stock' | 'asset' | 'expense' | 'ask'; onChange: (v: 'stock' | 'asset' | 'expense' | 'ask') => void }) => (
    <div className="mt-2 space-y-1">
      <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mb-1">
        {t('profile.categoryTrackingMode', 'Como rastrear esta categoria?')}
      </p>
      <div className="grid grid-cols-2 gap-1.5">
        {treatmentOptions.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-left text-xs transition-colors ${
              value === opt.value
                ? 'bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-400 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 font-medium'
                : 'bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'
            }`}
          >
            {opt.icon}
            <span className="truncate">{t(opt.labelKey)}</span>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900">
      <TopBar
        title={t('profile.settingsCategories')}
        onBack={onBack}
        className="bg-white dark:bg-slate-800 shadow-sm"
      />

      <div className="px-4 py-6 pb-24 space-y-6">

        {/* AGC: Built-in (default) categories - read-only */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-1">
            {t('profile.settingsCategoriesDefault')}
          </h3>
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
            {builtInCategories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0">
                  <Tag className="w-4 h-4" />
                </div>
                <span className="flex-1 text-sm text-gray-900 dark:text-gray-100">{cat.label}</span>
                <span className="text-[11px] text-gray-400 dark:text-slate-500 font-medium">
                  {t('inventory.manageCategoriesUnits.builtIn')}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* AGC: Custom categories - editable / removable */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 px-1">
            {t('profile.settingsCategoriesCustom')}
          </h3>

          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden">
            {customCategories.length === 0 && !isAdding && (
              <p className="px-4 py-4 text-sm text-gray-400 dark:text-slate-500 text-center">
                {t('profile.settingsCategoriesEmpty')}
              </p>
            )}

            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {customCategories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shrink-0">
                    <Tag className="w-4 h-4" />
                  </div>

                  {editingId === cat.id ? (
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="flex-1 px-2 py-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-900 dark:text-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit()
                            if (e.key === 'Escape') { setEditingId(null); setEditLabel('') }
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleSaveEdit}
                          disabled={!editLabel.trim()}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium disabled:opacity-50"
                        >
                          {t('common.save')}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditingId(null); setEditLabel('') }}
                          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <TreatmentSelector value={editStockTreatment} onChange={setEditStockTreatment} />
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <span className="text-sm text-gray-900 dark:text-gray-100">{cat.label}</span>
                        {/* AGC: Show small treatment badge below label */}
                        <span className={`ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          cat.stockTreatment === 'stock' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' :
                          cat.stockTreatment === 'asset' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' :
                          cat.stockTreatment === 'ask' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400' :
                          'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {cat.stockTreatment === 'stock' ? t('profile.trackingStock', 'Estoque') :
                           cat.stockTreatment === 'asset' ? t('profile.trackingAsset', 'Ativo') :
                           cat.stockTreatment === 'ask' ? t('profile.trackingAsk', 'Perguntar') :
                           t('profile.trackingExpense', 'Despesa/Receita')}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setEditingId(cat.id); setEditLabel(cat.label); setEditStockTreatment(cat.stockTreatment || 'expense') }}
                        className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                        aria-label={t('common.edit')}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemove(cat.id)}
                        className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        aria-label={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              ))}

              {/* AGC: Inline add-new row */}
              {isAdding && (
                <div className="px-4 py-3 bg-gray-50 dark:bg-slate-700/40">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      placeholder={t('inventory.manageCategoriesUnits.categoryNamePlaceholder')}
                      className="flex-1 px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveAdd()
                        if (e.key === 'Escape') { setIsAdding(false); setNewLabel(''); setNewStockTreatment('expense') }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSaveAdd}
                      disabled={!newLabel.trim()}
                      className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium disabled:opacity-50"
                    >
                      {t('common.save')}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setIsAdding(false); setNewLabel(''); setNewStockTreatment('expense') }}
                      className="px-3 py-2 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 text-sm"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                  <TreatmentSelector value={newStockTreatment} onChange={setNewStockTreatment} />
                </div>
              )}
            </div>
          </div>

          {/* AGC: Add new category button */}
          {!isAdding && (
            <button
              type="button"
              onClick={() => { setIsAdding(true); setNewLabel(''); setNewStockTreatment('expense') }}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border border-dashed border-emerald-400 dark:border-emerald-600 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('inventory.manageCategoriesUnits.addOne')}
            </button>
          )}
        </section>
      </div>
    </div>
  )
}
